import {
    useState, useEffect,
} from 'react';
import { faCircleInfo, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    NSCard, NSSelect, NSSwitch,
} from 'bricks';
import NSProFormaInput from 'bricks/NSProFormaInput/NSProFormaInput';
import RenderIf from 'components/RenderIf/RenderIf';
import { PRO_FORMA_SIZE_UNIT_OPTIONS, SQUARE_FEET_PER_ACRE, UnitTypeIds } from 'constants/unitTypes';
import { CALCULATE_SIZE_HEADER_CLASS_NAME } from 'views/ProFormaTable/constants';
import { customStyles } from 'bricks/NSSelect/NSSelect';
import { numeralFormatter } from 'ns_libs/formatter';
import { IGrossBuildableAreaLot } from 'views/ProFormaTable/types';
import './CalculateSizeCard.scss';
import { Tooltip } from 'reactstrap';
import { AreaMode } from 'views/ProFormaSetup/constants';

interface ICalculateSizeCardProps {
    lots: IGrossBuildableAreaLot[];
    totalGrossBuildableArea: number;
    onCreateLot: () => void;
    onDeleteLot: (id: number) => void;
    onUpdateLot: (id: number, dataKey: keyof IGrossBuildableAreaLot, value: string | number) => void;
    cardLevel?: number;
}

const CalculateSizeCard = ({
    lots,
    totalGrossBuildableArea,
    onCreateLot,
    onDeleteLot,
    onUpdateLot,
    cardLevel = 3,
}: ICalculateSizeCardProps) => {
    const [temporaryLots, setTemporaryLots] = useState<IGrossBuildableAreaLot[]>(lots);
    const [showTooltip, setShowTooltip] = useState(false);

    const shouldShowFarColumn = temporaryLots.some(lot => lot.areaMode === AreaMode.FAR);
    const totalLandAreaInFeet = temporaryLots // Update with total value calculated once backend is ready
        .reduce((sum, lot) => sum + (lot.landAreaUnitTypeId === UnitTypeIds.ACRE ? lot.landArea * SQUARE_FEET_PER_ACRE : lot.landArea), 0);
    const toggleTooltip = () => setShowTooltip(prev => !prev);

    const handleInputChange = (id: number, key: keyof IGrossBuildableAreaLot, value: string | number) => {
        setTemporaryLots(prev => prev.map(lot => (lot.id === id ? { ...lot, [key]: value } : lot)));
    };

    const commitChange = (id: number, key: keyof IGrossBuildableAreaLot, value: string | number) => {
        onUpdateLot(id, key, value);
        handleInputChange(id, key, value);
    };

    useEffect(() => {
        setTemporaryLots(lots);
    }, [lots]);

    return (
        <NSCard className={`NSCard--level-${cardLevel} p-3 mt-1 mb-2 CalculateSizeCard`}>
            <table>
                <thead>
                    <tr>
                        <th className={CALCULATE_SIZE_HEADER_CLASS_NAME}>Lot&nbsp;</th>
                        <th className={CALCULATE_SIZE_HEADER_CLASS_NAME}>
                            Use FAR
                            <FontAwesomeIcon
                                className="pl-1 text-white"
                                id="calculate-toggle-tooltip"
                                data-testid="calculate-toggle-tooltip"
                                icon={faCircleInfo}
                            />
                            <Tooltip target="calculate-toggle-tooltip" isOpen={showTooltip} toggle={toggleTooltip} autohide={false}>
                                Calculate gross buildable area of assemblage
                            </Tooltip>
                        </th>
                        <th className={CALCULATE_SIZE_HEADER_CLASS_NAME}>Land area</th>
                        <RenderIf isTrue={shouldShowFarColumn}>
                            <th className={CALCULATE_SIZE_HEADER_CLASS_NAME}>FAR</th>
                        </RenderIf>
                        <th className={CALCULATE_SIZE_HEADER_CLASS_NAME}>Gross buildable area</th>
                        <th className="p-0 pb-1" />
                    </tr>
                </thead>
                <tbody>
                    {temporaryLots.map((lot, index) => {
                        const isFAR = lot.areaMode === AreaMode.FAR;
                        return (
                            <tr key={lot.id} className="pt-1">
                                <td className="p-0 pb-1 pr-1">{index + 1}</td>
                                <td className="p-0 pb-1 pr-1">
                                    <NSSwitch
                                        id={`calculateToggle-${lot.id}`}
                                        name={`calculateToggle-${lot.id}`}
                                        checked={isFAR}
                                        onChange={() => {
                                            const newAreaMode = isFAR ? AreaMode.GROSS_BUILD_AREA : AreaMode.FAR;
                                            handleInputChange(lot.id, 'areaMode', newAreaMode);
                                            onUpdateLot(lot.id, 'areaMode', newAreaMode);
                                        }}
                                        containerClassName="text-dark"
                                    />
                                </td>
                                <td className="p-0 pb-1 pr-1">
                                    <NSProFormaInput
                                        type="number"
                                        name="landArea"
                                        customClassName="NSInput__appended-child--with-select"
                                        value={lot.landArea}
                                        handleUpdateValue={value => {
                                            handleInputChange(lot.id, 'landArea', value);
                                            commitChange(lot.id, 'landArea', Number(value));
                                        }}
                                        placeholder="Enter land area"
                                        inputGroupClassName="p-0"
                                        appendInputAddon={(
                                            <NSSelect
                                                name="landAreaUnitTypeId"
                                                options={PRO_FORMA_SIZE_UNIT_OPTIONS}
                                                value={PRO_FORMA_SIZE_UNIT_OPTIONS.find(opt => opt.value === lot.landAreaUnitTypeId)}
                                                onChange={option => {
                                                    handleInputChange(lot.id, 'landAreaUnitTypeId', option.value);
                                                    onUpdateLot(lot.id, 'landAreaUnitTypeId', option.value);
                                                }}
                                                isClearable={false}
                                                isSearchable={false}
                                                styles={{
                                                    ...customStyles,
                                                    control: styles => ({
                                                        ...styles,
                                                        border: 'none',
                                                        backgroundColor: '#47515d',
                                                        width: '80px',
                                                    }),
                                                }}
                                            />
                                        )}
                                    />
                                </td>
                                <RenderIf isTrue={shouldShowFarColumn}>
                                    <RenderIf isTrue={isFAR}>
                                        <td className="p-0 pb-1 pr-1">
                                            <NSProFormaInput
                                                type="number"
                                                name="floorAreaRatio"
                                                customClassName="CalculateSizeCard__input NSInput__appended-child--with-select"
                                                value={lot.floorAreaRatio}
                                                handleUpdateValue={value => {
                                                    handleInputChange(lot.id, 'floorAreaRatio', value);
                                                    commitChange(lot.id, 'floorAreaRatio', Number(value));
                                                }}
                                                placeholder="Enter FAR"
                                                inputGroupClassName="p-0"
                                                step={0.1}
                                            />
                                        </td>
                                    </RenderIf>
                                    <RenderIf isTrue={!isFAR}>
                                        <td className="CalculateSizeCard__background-cell d-flex align-items-center justify-content-start mr-1">
                                            <span className="text-muted pl-2">-</span>
                                        </td>
                                    </RenderIf>
                                </RenderIf>
                                <RenderIf isTrue={!isFAR}>
                                    <td className="p-0 pb-1 pr-1">
                                        <NSProFormaInput
                                            type="number"
                                            name="grossBuildableArea"
                                            customClassName="NSInput__appended-child--with-select"
                                            value={lot.grossBuildableArea}
                                            handleUpdateValue={value => {
                                                handleInputChange(lot.id, 'grossBuildableArea', value);
                                                commitChange(lot.id, 'grossBuildableArea', Number(value));
                                            }}
                                            placeholder="Enter gross buildable area"
                                            inputGroupClassName="p-0"
                                            appendInputAddon={<div className="px-2">ft²</div>}
                                        />
                                    </td>
                                </RenderIf>
                                <RenderIf isTrue={isFAR}>
                                    <td className="CalculateSizeCard__bordered-cell d-flex align-items-center justify-content-between mr-1">
                                        <span className="text-dark pl-2">
                                            {numeralFormatter(
                                                lot.landArea
                                                * lot.floorAreaRatio
                                                * (lot.landAreaUnitTypeId === UnitTypeIds.ACRE ? SQUARE_FEET_PER_ACRE : 1),
                                            )}
                                            &nbsp;
                                        </span>
                                        <span className="text-muted">ft²</span>
                                    </td>
                                </RenderIf>
                                <RenderIf isTrue={temporaryLots.length > 1}>
                                    <td className="p-0 pb-1 pl-1 text-center">
                                        <FontAwesomeIcon
                                            icon={faTrash}
                                            size="sm"
                                            className="text-dark cursor--pointer"
                                            data-testid={`delete-lot-${lot.id}`}
                                            onClick={() => onDeleteLot(lot.id)}
                                        />
                                    </td>
                                </RenderIf>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            <div className="text-primary mt-1 mb-0">
                <span role="presentation" className="cursor--pointer d-inline-flex align-items-center" onClick={() => onCreateLot()}>
                    <FontAwesomeIcon icon={faPlus} size="sm" className="mr-1" />
                    Add lot
                </span>
            </div>

            <RenderIf isTrue={temporaryLots.length > 1}>
                <hr className="mt-1 mb-2" />
                <div className="d-flex justify-content-between">
                    <div className="text-dark">Land area:</div>
                    <div>
                        <span className="text-dark">
                            {numeralFormatter(totalLandAreaInFeet, '0,0.[00]')}
                            {' '}
                        </span>
                        <span>ft&sup2;</span>
                    </div>
                    <div className="text-dark">Gross buildable area:</div>
                    <div>
                        <span className="text-dark">
                            {numeralFormatter(totalGrossBuildableArea, '0,0.[00]')}
                            {' '}
                        </span>
                        <span>ft&sup2;</span>
                    </div>
                </div>
            </RenderIf>
        </NSCard>
    );
};

export default CalculateSizeCard;
